import * as React from 'react';
import { motion } from 'framer-motion';
import {
  Package,
  ArrowDownToLine,
  ClipboardEdit,
  History,
  Calendar,
  Tag,
  AlertTriangle,
  Search,
  Printer,
  TrendingDown,
  XCircle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/ui/data-table';
import { RequireRole } from '@/lib/auth';
import { apiFetch } from '@/lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  barcode: string;
  internal_barcode: string | null;
  name: string;
  sale_price: number;
  stock_quantity: number;
  min_stock_level: number;
  status: 'normal' | 'baixo' | 'esgotado';
  unit: string;
  category: string;
}

interface StockMovement {
  id: string;
  product_id: string;
  product_name: string;
  type: 'entrada' | 'saida' | 'ajuste';
  quantity: number;
  reason: string;
  user_id: string;
  created_at: string;
}

interface ProductBatch {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  expiry_date: string;
  status: 'valido' | 'proximo_vencimento' | 'vencido';
  barcode?: string;
}

interface LabelData {
  product_id: string;
  name: string;
  barcode: string;
  price: number;
  unit: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type TabId = 'estoque' | 'entradas' | 'ajustes' | 'movimentacoes' | 'validade' | 'etiquetas';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'destructive'> = {
  normal: 'success',
  baixo: 'warning',
  esgotado: 'destructive',
};

const STATUS_LABELS: Record<string, string> = {
  normal: 'Normal',
  baixo: 'Baixo',
  esgotado: 'Esgotado',
};

const BATCH_STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'destructive'> = {
  valido: 'success',
  proximo_vencimento: 'warning',
  vencido: 'destructive',
};

const BATCH_STATUS_LABELS: Record<string, string> = {
  valido: 'Válido',
  proximo_vencimento: 'Próx. Vencimento',
  vencido: 'Vencido',
};

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  entrada: 'Entrada',
  saida: 'Saída',
  ajuste: 'Ajuste',
};

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR');
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// ─── Tab Button ──────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-base font-medium rounded-lg transition-all ${
        active
          ? 'bg-primary/15 text-primary border border-primary/30'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ─── Confirmation Modal ──────────────────────────────────────────────────────

function ConfirmModal({
  open,
  title,
  message,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
        <p className="text-base text-muted-foreground mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onConfirm}>Confirmar</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Summary Cards ───────────────────────────────────────────────────────────

function SummaryCards({ products }: { products: Product[] }) {
  const total = products.length;
  const baixo = products.filter((p) => p.status === 'baixo').length;
  const esgotado = products.filter((p) => p.status === 'esgotado').length;
  const normal = products.filter((p) => p.status === 'normal').length;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          <Package className="h-4 w-4" />
          <span className="text-sm">Total de Produtos</span>
        </div>
        <span className="text-2xl font-bold text-foreground">{total}</span>
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-green-500 mb-1">
          <Package className="h-4 w-4" />
          <span className="text-sm">Estoque Normal</span>
        </div>
        <span className="text-2xl font-bold text-green-500">{normal}</span>
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-yellow-500 mb-1">
          <TrendingDown className="h-4 w-4" />
          <span className="text-sm">Estoque Baixo</span>
        </div>
        <span className="text-2xl font-bold text-yellow-500">{baixo}</span>
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-red-500 mb-1">
          <XCircle className="h-4 w-4" />
          <span className="text-sm">Esgotados</span>
        </div>
        <span className="text-2xl font-bold text-red-500">{esgotado}</span>
      </div>
    </div>
  );
}


// ─── Estoque Tab ─────────────────────────────────────────────────────────────

function EstoqueTab() {
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const debouncedSearch = useDebounce(search, 300);

  const fetchProducts = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (categoryFilter) params.set('category', categoryFilter);
      const response = await apiFetch(`/api/products?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.data);
      }
    } catch {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoryFilter]);

  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const categories = React.useMemo(() => {
    const cats = new Set(products.map((p) => p.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [products]);

  const columns: Column<Product>[] = [
    { key: 'name', header: 'Produto', sortable: true, render: (row) => <span className="font-medium text-base">{row.name}</span> },
    { key: 'barcode', header: 'Código', render: (row) => <span className="font-mono text-sm">{row.barcode}</span> },
    { key: 'category', header: 'Categoria', render: (row) => <span className="text-base">{row.category}</span> },
    { key: 'unit', header: 'Unidade', render: (row) => <span className="uppercase text-sm">{row.unit}</span> },
    {
      key: 'stock_quantity',
      header: 'Estoque',
      sortable: true,
      render: (row) => <span className="font-semibold text-base">{Number(row.stock_quantity)}</span>,
    },
    {
      key: 'min_stock_level',
      header: 'Nível Mín.',
      render: (row) => <span className="text-muted-foreground text-base">{Number(row.min_stock_level)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={STATUS_VARIANTS[row.status] || 'default'} dot>
          {STATUS_LABELS[row.status] || row.status}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <SummaryCards products={products} />

      {/* Alerta de estoque baixo */}
      {products.some((p) => p.status === 'baixo' || p.status === 'esgotado') && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
          <span className="text-base text-yellow-200">
            {products.filter((p) => p.status === 'esgotado').length > 0 &&
              `${products.filter((p) => p.status === 'esgotado').length} produto(s) esgotado(s). `}
            {products.filter((p) => p.status === 'baixo').length > 0 &&
              `${products.filter((p) => p.status === 'baixo').length} produto(s) com estoque baixo.`}
          </span>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar produto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-base placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-11 rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          <option value="">Todas categorias</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <DataTable columns={columns} data={products} getRowKey={(row) => row.id} emptyMessage="Nenhum produto encontrado" />
      )}
    </div>
  );
}

// ─── Entradas Tab ────────────────────────────────────────────────────────────

function EntradasTab() {
  const [products, setProducts] = React.useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = React.useState('');
  const [quantity, setQuantity] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [searchProduct, setSearchProduct] = React.useState('');

  React.useEffect(() => {
    async function load() {
      try {
        const response = await apiFetch('/api/products?limit=200');
        if (response.ok) {
          const data = await response.json();
          setProducts(data.data);
        }
      } catch {
        // silent
      }
    }
    load();
  }, []);

  const filteredProducts = React.useMemo(() => {
    if (!searchProduct) return products;
    const term = searchProduct.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(term) || p.barcode.includes(term));
  }, [products, searchProduct]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProduct || !quantity || Number(quantity) <= 0) {
      toast.error('Selecione um produto e informe a quantidade');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/api/inventory/entry', {
        method: 'POST',
        body: JSON.stringify({
          product_id: selectedProduct,
          quantity: Number(quantity),
          reason: reason || undefined,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao registrar entrada');
      }

      const prod = products.find((p) => p.id === selectedProduct);
      toast.success(`Entrada registrada: +${quantity} un. de "${prod?.name || 'produto'}"`);
      setSelectedProduct('');
      setQuantity('');
      setReason('');
      setSearchProduct('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-lg">
      <h3 className="text-xl font-semibold mb-4">Registrar Entrada de Mercadoria</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="entry-search" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Buscar Produto
          </label>
          <input
            id="entry-search"
            type="text"
            value={searchProduct}
            onChange={(e) => setSearchProduct(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            placeholder="Digite o nome ou código..."
          />
        </div>
        <div>
          <label htmlFor="entry-product" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Produto
          </label>
          <select
            id="entry-product"
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          >
            <option value="">Selecione um produto</option>
            {filteredProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} (Estoque: {Number(p.stock_quantity)})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="entry-quantity" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Quantidade
          </label>
          <input
            id="entry-quantity"
            type="number"
            step="0.001"
            min="0.001"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            placeholder="Ex: 50"
          />
        </div>
        <div>
          <label htmlFor="entry-reason" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Observação (opcional)
          </label>
          <input
            id="entry-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            placeholder="Ex: Compra fornecedor X"
          />
        </div>
        <Button type="submit" disabled={submitting} className="w-full h-12 text-base">
          {submitting ? 'Registrando...' : 'Registrar Entrada'}
        </Button>
      </form>
    </div>
  );
}


// ─── Ajustes Tab ─────────────────────────────────────────────────────────────

function AjustesTab() {
  const [products, setProducts] = React.useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = React.useState('');
  const [newQuantity, setNewQuantity] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [searchProduct, setSearchProduct] = React.useState('');

  React.useEffect(() => {
    async function load() {
      try {
        const response = await apiFetch('/api/products?limit=200');
        if (response.ok) {
          const data = await response.json();
          setProducts(data.data);
        }
      } catch {
        // silent
      }
    }
    load();
  }, []);

  const selectedProductData = products.find((p) => p.id === selectedProduct);

  const filteredProducts = React.useMemo(() => {
    if (!searchProduct) return products;
    const term = searchProduct.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(term) || p.barcode.includes(term));
  }, [products, searchProduct]);

  function handlePreSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProduct || newQuantity === '' || Number(newQuantity) < 0) {
      toast.error('Selecione um produto e informe a nova quantidade');
      return;
    }
    if (!reason.trim()) {
      toast.error('Informe o motivo do ajuste');
      return;
    }
    setShowConfirm(true);
  }

  async function handleConfirm() {
    setShowConfirm(false);
    setSubmitting(true);
    try {
      const response = await apiFetch('/api/inventory/adjustment', {
        method: 'POST',
        body: JSON.stringify({
          product_id: selectedProduct,
          new_quantity: Number(newQuantity),
          reason,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao ajustar estoque');
      }

      const prod = selectedProductData;
      const oldQty = Number(prod?.stock_quantity || 0);
      const diff = Number(newQuantity) - oldQty;
      toast.success(
        `Ajuste realizado: "${prod?.name}" ${oldQty} → ${newQuantity} (${diff >= 0 ? '+' : ''}${diff})`
      );
      setSelectedProduct('');
      setNewQuantity('');
      setReason('');
      setSearchProduct('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-lg">
      <h3 className="text-xl font-semibold mb-4">Ajuste de Inventário</h3>

      <ConfirmModal
        open={showConfirm}
        title="Confirmar Ajuste de Estoque"
        message={
          selectedProductData
            ? `Deseja alterar o estoque de "${selectedProductData.name}" de ${Number(selectedProductData.stock_quantity)} para ${newQuantity}?\n\nMotivo: ${reason}`
            : 'Confirmar ajuste?'
        }
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
      />

      <form onSubmit={handlePreSubmit} className="space-y-4">
        <div>
          <label htmlFor="adjust-search" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Buscar Produto
          </label>
          <input
            id="adjust-search"
            type="text"
            value={searchProduct}
            onChange={(e) => setSearchProduct(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            placeholder="Digite o nome ou código..."
          />
        </div>
        <div>
          <label htmlFor="adjust-product" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Produto
          </label>
          <select
            id="adjust-product"
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          >
            <option value="">Selecione um produto</option>
            {filteredProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} (Estoque atual: {Number(p.stock_quantity)})
              </option>
            ))}
          </select>
        </div>
        {selectedProductData && (
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-base">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-muted-foreground">Estoque atual:</span>{' '}
                <span className="font-bold text-lg">{Number(selectedProductData.stock_quantity)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Nível mínimo:</span>{' '}
                <span className="font-semibold">{Number(selectedProductData.min_stock_level)}</span>
              </div>
            </div>
            <div className="mt-2">
              <Badge variant={STATUS_VARIANTS[selectedProductData.status] || 'default'} dot>
                {STATUS_LABELS[selectedProductData.status] || selectedProductData.status}
              </Badge>
            </div>
          </div>
        )}
        <div>
          <label htmlFor="adjust-quantity" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Nova Quantidade
          </label>
          <input
            id="adjust-quantity"
            type="number"
            step="0.001"
            min="0"
            value={newQuantity}
            onChange={(e) => setNewQuantity(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            placeholder="Nova quantidade em estoque"
          />
          {selectedProductData && newQuantity !== '' && (
            <p className="mt-1 text-sm text-muted-foreground">
              Diferença: {Number(newQuantity) - Number(selectedProductData.stock_quantity) >= 0 ? '+' : ''}
              {Number(newQuantity) - Number(selectedProductData.stock_quantity)}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="adjust-reason" className="mb-1.5 block text-sm font-medium text-muted-foreground">
            Motivo do Ajuste *
          </label>
          <input
            id="adjust-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            placeholder="Ex: Contagem física, Perda, Avaria..."
          />
        </div>
        <Button type="submit" disabled={submitting} className="w-full h-12 text-base">
          {submitting ? 'Ajustando...' : 'Confirmar Ajuste'}
        </Button>
      </form>
    </div>
  );
}

// ─── Movimentações Tab ───────────────────────────────────────────────────────

function MovimentacoesTab() {
  const [movements, setMovements] = React.useState<StockMovement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [pagination, setPagination] = React.useState<Pagination>({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [typeFilter, setTypeFilter] = React.useState('');

  const fetchMovements = React.useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (typeFilter) params.set('type', typeFilter);
      const response = await apiFetch(`/api/inventory/movements?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setMovements(data.data);
        setPagination(data.pagination);
      }
    } catch {
      toast.error('Erro ao carregar movimentações');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  React.useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const columns: Column<StockMovement>[] = [
    {
      key: 'created_at',
      header: 'Data/Hora',
      render: (row) => <span className="text-sm">{formatDateTime(row.created_at)}</span>,
    },
    { key: 'product_name', header: 'Produto', render: (row) => <span className="font-medium text-base">{row.product_name}</span> },
    {
      key: 'type',
      header: 'Tipo',
      render: (row) => {
        const variant = row.type === 'entrada' ? 'success' : row.type === 'saida' ? 'destructive' : 'warning';
        return <Badge variant={variant}>{MOVEMENT_TYPE_LABELS[row.type] || row.type}</Badge>;
      },
    },
    {
      key: 'quantity',
      header: 'Quantidade',
      render: (row) => (
        <span className={`font-semibold text-base ${Number(row.quantity) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {Number(row.quantity) > 0 ? '+' : ''}{Number(row.quantity)}
        </span>
      ),
    },
    { key: 'reason', header: 'Motivo', render: (row) => <span className="text-muted-foreground text-sm">{row.reason || '—'}</span> },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-11 rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          <option value="">Todos os tipos</option>
          <option value="entrada">Entrada</option>
          <option value="saida">Saída</option>
          <option value="ajuste">Ajuste</option>
        </select>
        <span className="text-sm text-muted-foreground">
          {pagination.total} movimentação(ões)
        </span>
      </div>
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <DataTable columns={columns} data={movements} getRowKey={(row) => row.id} emptyMessage="Nenhuma movimentação encontrada" />
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => fetchMovements(pagination.page - 1)}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => fetchMovements(pagination.page + 1)}>
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─── Validade Tab ────────────────────────────────────────────────────────────

function ValidadeTab() {
  const [batches, setBatches] = React.useState<ProductBatch[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [daysFilter, setDaysFilter] = React.useState('30');
  const [products, setProducts] = React.useState<Product[]>([]);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [newBatch, setNewBatch] = React.useState({ product_id: '', quantity: '', expiry_date: '' });
  const [submitting, setSubmitting] = React.useState(false);

  const fetchBatches = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/inventory/batches?limit=100');
      if (response.ok) {
        const data = await response.json();
        setBatches(data.data);
      }
    } catch {
      toast.error('Erro ao carregar lotes');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchBatches();
    async function loadProducts() {
      try {
        const response = await apiFetch('/api/products?limit=200');
        if (response.ok) {
          const data = await response.json();
          setProducts(data.data);
        }
      } catch {
        // silent
      }
    }
    loadProducts();
  }, [fetchBatches]);

  async function handleAddBatch(e: React.FormEvent) {
    e.preventDefault();
    if (!newBatch.product_id || !newBatch.quantity || !newBatch.expiry_date) {
      toast.error('Preencha todos os campos');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/api/inventory/batches', {
        method: 'POST',
        body: JSON.stringify({
          product_id: newBatch.product_id,
          quantity: Number(newBatch.quantity),
          expiry_date: newBatch.expiry_date,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao criar lote');
      }

      toast.success('Lote cadastrado com sucesso');
      setNewBatch({ product_id: '', quantity: '', expiry_date: '' });
      setShowAddForm(false);
      fetchBatches();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  const filteredBatches = batches.filter((b) => {
    if (daysFilter === 'all') return true;
    const days = Number(daysFilter);
    const today = new Date();
    const expiry = new Date(b.expiry_date);
    const diff = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff <= days;
  });

  const vencidos = batches.filter((b) => b.status === 'vencido').length;
  const proximoVencimento = batches.filter((b) => b.status === 'proximo_vencimento').length;

  const columns: Column<ProductBatch>[] = [
    { key: 'product_name', header: 'Produto', render: (row) => <span className="font-medium text-base">{row.product_name}</span> },
    { key: 'quantity', header: 'Quantidade', render: (row) => <span className="text-base">{Number(row.quantity)}</span> },
    {
      key: 'expiry_date',
      header: 'Data de Validade',
      render: (row) => {
        const today = new Date();
        const expiry = new Date(row.expiry_date);
        const diff = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return (
          <span className={`text-base ${diff <= 0 ? 'text-red-400 font-semibold' : diff <= 7 ? 'text-yellow-400 font-medium' : ''}`}>
            {formatDate(row.expiry_date)}
            {diff <= 0 && ' (Vencido)'}
            {diff > 0 && diff <= 7 && ` (${diff} dias)`}
            {diff > 7 && diff <= 30 && ` (${diff} dias)`}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={BATCH_STATUS_VARIANTS[row.status] || 'default'} dot>
          {BATCH_STATUS_LABELS[row.status] || row.status}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      {/* Resumo de validade */}
      {(vencidos > 0 || proximoVencimento > 0) && (
        <div className="mb-4 flex items-center gap-4 flex-wrap">
          {vencidos > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-300">{vencidos} lote(s) vencido(s)</span>
            </div>
          )}
          {proximoVencimento > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span className="text-sm text-yellow-300">{proximoVencimento} lote(s) próximo(s) do vencimento</span>
            </div>
          )}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            value={daysFilter}
            onChange={(e) => setDaysFilter(e.target.value)}
            className="h-11 rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          >
            <option value="7">Vencendo em 7 dias</option>
            <option value="15">Vencendo em 15 dias</option>
            <option value="30">Vencendo em 30 dias</option>
            <option value="all">Todos os lotes</option>
          </select>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)} variant={showAddForm ? 'outline' : 'default'}>
          {showAddForm ? 'Cancelar' : 'Novo Lote'}
        </Button>
      </div>

      {showAddForm && (
        <div className="mb-4 rounded-lg border border-border bg-muted/20 p-4">
          <h4 className="text-base font-semibold mb-3">Cadastrar Novo Lote</h4>
          <form onSubmit={handleAddBatch} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              value={newBatch.product_id}
              onChange={(e) => setNewBatch((p) => ({ ...p, product_id: e.target.value }))}
              className="h-11 rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            >
              <option value="">Produto</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <input
              type="number"
              step="0.001"
              min="0.001"
              placeholder="Quantidade"
              value={newBatch.quantity}
              onChange={(e) => setNewBatch((p) => ({ ...p, quantity: e.target.value }))}
              className="h-11 rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            />
            <input
              type="date"
              value={newBatch.expiry_date}
              onChange={(e) => setNewBatch((p) => ({ ...p, expiry_date: e.target.value }))}
              className="h-11 rounded-lg border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            />
            <Button type="submit" disabled={submitting} className="h-11">
              {submitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <DataTable columns={columns} data={filteredBatches} getRowKey={(row) => row.id} emptyMessage="Nenhum lote encontrado" />
      )}
    </div>
  );
}


// ─── Etiquetas Tab ───────────────────────────────────────────────────────────

function EtiquetasTab() {
  const [products, setProducts] = React.useState<Product[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [labels, setLabels] = React.useState<LabelData[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [searchLabel, setSearchLabel] = React.useState('');
  const printFrameRef = React.useRef<HTMLIFrameElement>(null);

  React.useEffect(() => {
    async function load() {
      try {
        const response = await apiFetch('/api/products?limit=200');
        if (response.ok) {
          const data = await response.json();
          setProducts(data.data);
        }
      } catch {
        toast.error('Erro ao carregar produtos');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filteredProducts = React.useMemo(() => {
    if (!searchLabel) return products;
    const term = searchLabel.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(term) || p.barcode.includes(term));
  }, [products, searchLabel]);

  function toggleProduct(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function selectAll() {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map((p) => p.id)));
    }
  }

  async function generateBarcode(productId: string) {
    try {
      const response = await apiFetch(`/api/inventory/internal-barcode/${productId}`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        if (data.already_exists) {
          toast.info(`Código interno já existe: ${data.internal_barcode}`);
        } else {
          toast.success(`Código interno gerado: ${data.internal_barcode}`);
        }
        const resp = await apiFetch('/api/products?limit=200');
        if (resp.ok) {
          const d = await resp.json();
          setProducts(d.data);
        }
      }
    } catch {
      toast.error('Erro ao gerar código interno');
    }
  }

  async function generateLabels() {
    if (selectedIds.size === 0) {
      toast.error('Selecione ao menos um produto');
      return;
    }

    setGenerating(true);
    try {
      const response = await apiFetch('/api/inventory/labels', {
        method: 'POST',
        body: JSON.stringify({ product_ids: Array.from(selectedIds) }),
      });

      if (response.ok) {
        const data = await response.json();
        setLabels(data.data);
        toast.success(`${data.data.length} etiqueta(s) gerada(s)`);
      }
    } catch {
      toast.error('Erro ao gerar etiquetas');
    } finally {
      setGenerating(false);
    }
  }

  function handlePrint() {
    if (labels.length === 0) return;

    const labelsHtml = labels
      .map(
        (l) => `
        <div style="border:1px solid #ccc;padding:12px;margin:8px;display:inline-block;width:200px;font-family:monospace;text-align:center;">
          <div style="font-size:14px;font-weight:bold;margin-bottom:4px;">${l.name}</div>
          <div style="font-size:20px;font-weight:bold;margin:8px 0;">${formatBRL(l.price)}</div>
          <div style="font-size:11px;margin-bottom:4px;">${l.unit.toUpperCase()}</div>
          <div style="font-size:12px;letter-spacing:2px;border-top:1px solid #eee;padding-top:4px;">${l.barcode}</div>
        </div>
      `,
      )
      .join('');

    const htmlContent = `
      <html>
        <head><title>Etiquetas - FluxoPDV</title></head>
        <body style="padding:20px;margin:0;">
          <div style="display:flex;flex-wrap:wrap;">${labelsHtml}</div>
        </body>
      </html>
    `;

    // Usar iframe oculto para impressão (sem popup)
    const iframe = printFrameRef.current;
    if (iframe) {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(htmlContent);
        doc.close();
        setTimeout(() => {
          iframe.contentWindow?.print();
        }, 300);
      }
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      {/* Iframe oculto para impressão */}
      <iframe ref={printFrameRef} className="hidden" title="print-labels" />

      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="relative min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar produto..."
              value={searchLabel}
              onChange={(e) => setSearchLabel(e.target.value)}
              className="flex h-11 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-base placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            />
          </div>
          <Button variant="outline" size="sm" onClick={selectAll}>
            {selectedIds.size === filteredProducts.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
          </Button>
          <span className="text-sm text-muted-foreground">{selectedIds.size} selecionado(s)</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={generateLabels} disabled={generating || selectedIds.size === 0}>
            <Tag className="h-4 w-4 mr-1" />
            {generating ? 'Gerando...' : 'Gerar Etiquetas'}
          </Button>
          {labels.length > 0 && (
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-1" />
              Imprimir
            </Button>
          )}
        </div>
      </div>

      {/* Product selection list */}
      <div className="rounded-lg border border-border overflow-hidden mb-4">
        <table className="w-full text-base">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="h-11 px-4 text-left w-10"></th>
              <th className="h-11 px-4 text-left font-medium text-muted-foreground">Produto</th>
              <th className="h-11 px-4 text-left font-medium text-muted-foreground">Código</th>
              <th className="h-11 px-4 text-left font-medium text-muted-foreground">Cód. Interno</th>
              <th className="h-11 px-4 text-left font-medium text-muted-foreground">Preço</th>
              <th className="h-11 px-4 text-left font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((p, idx) => (
              <tr key={p.id} className={`border-b border-border/50 hover:bg-accent/50 ${idx % 2 === 1 ? 'bg-muted/20' : ''}`}>
                <td className="px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggleProduct(p.id)}
                    className="h-4 w-4 rounded border-border"
                    aria-label={`Selecionar ${p.name}`}
                  />
                </td>
                <td className="px-4 py-2.5 font-medium">{p.name}</td>
                <td className="px-4 py-2.5 font-mono text-sm">{p.barcode}</td>
                <td className="px-4 py-2.5 font-mono text-sm">{p.internal_barcode || '—'}</td>
                <td className="px-4 py-2.5">{formatBRL(Number(p.sale_price))}</td>
                <td className="px-4 py-2.5">
                  {!p.internal_barcode && (
                    <Button variant="outline" size="sm" onClick={() => generateBarcode(p.id)}>
                      Gerar Código
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Generated labels preview */}
      {labels.length > 0 && (
        <div>
          <h4 className="text-base font-semibold mb-3">Pré-visualização das Etiquetas</h4>
          <div className="flex flex-wrap gap-3">
            {labels.map((l) => (
              <div
                key={l.product_id}
                className="rounded-lg border border-border bg-white p-3 text-center w-48 text-black"
              >
                <div className="text-xs font-bold truncate">{l.name}</div>
                <div className="text-lg font-bold my-1">{formatBRL(l.price)}</div>
                <div className="text-[10px] text-gray-500 uppercase">{l.unit}</div>
                <div className="text-xs font-mono mt-1 tracking-wider border-t pt-1">{l.barcode}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function InventoryPageContent() {
  const [activeTab, setActiveTab] = React.useState<TabId>('estoque');

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'estoque', label: 'Estoque', icon: Package },
    { id: 'entradas', label: 'Entradas', icon: ArrowDownToLine },
    { id: 'ajustes', label: 'Ajustes', icon: ClipboardEdit },
    { id: 'movimentacoes', label: 'Movimentações', icon: History },
    { id: 'validade', label: 'Validade', icon: Calendar },
    { id: 'etiquetas', label: 'Etiquetas', icon: Tag },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Package className="h-6 w-6 text-primary" />
          Gestão de Estoque
        </h1>
        <p className="mt-1 text-base text-muted-foreground">
          Controle de estoque, validade de produtos e geração de etiquetas
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            icon={tab.icon}
            label={tab.label}
          />
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'estoque' && <EstoqueTab />}
      {activeTab === 'entradas' && <EntradasTab />}
      {activeTab === 'ajustes' && <AjustesTab />}
      {activeTab === 'movimentacoes' && <MovimentacoesTab />}
      {activeTab === 'validade' && <ValidadeTab />}
      {activeTab === 'etiquetas' && <EtiquetasTab />}
    </div>
  );
}

export function InventoryPage() {
  return (
    <RequireRole roles={['gerente']}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <InventoryPageContent />
      </motion.div>
    </RequireRole>
  );
}
